import { apiErrorMessage } from '@agos/api-client'
import {
  getGetNotificationsQueryKey,
  getGetNotificationsUnreadCountQueryKey,
  useGetNotifications,
  useGetNotificationsUnreadCount,
  usePostNotificationsDailyCheck,
  usePostNotificationsIdRead,
  usePostNotificationsReadAll,
} from '@agos/api-client/notifications'
import { T, formatCount, formatDateTime, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, EmptyState, PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  Eye,
  Info,
  RefreshCw,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'wouter'

import { mayWrite } from '../write-access'
import {
  PAGE_SIZE,
  countsDescription,
  dailyCheckWarning,
  inboxSummary,
  hasMore,
  isLabelledKind,
  kindLabel,
  linkFor,
  nextPageSize,
  pageOf,
  type Notification,
  type Severity,
} from './notification-inbox'

/**
 * `/notifications` — the staff inbox.
 *
 * Not `/settings-notifications`, which decides how *customers* are told things. This one is what the system
 * noticed: low stock, an invoice past due, a purchase order it drafted. Same controller, different job, so
 * two pages.
 *
 * **FB-146.** The unread count was computed from the loaded page and printed beside the server's total, so
 * one sentence carried two different populations. `GET /notifications/unread-count` answers the real figure
 * and is used.
 *
 * **FB-147.** `<T>{n.title}</T>` put generated data through the translation memory — every notification a
 * dictionary key, and a call to the missing-translation endpoint for each. The titles are written in Slovak
 * by the backend; they are rendered as text.
 *
 * **And the daily check says what it does first.** `DailyCheck.run()` drafts purchase orders and e-mails
 * every administrator; legacy's button fires on the first click.
 *
 * Ported from legacy `pages/notifications.tsx` (185 lines).
 */

export function NotificationsPage() {
  const [offset, setOffset] = useState(0)
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [loaded, setLoaded] = useState<Notification[]>([])
  const [confirmCheck, setConfirmCheck] = useState(false)

  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const list = useGetNotifications({
    limit: PAGE_SIZE,
    offset,
    ...(unreadOnly ? { unread: 'true' } : {}),
  })
  const unread = useGetNotificationsUnreadCount()

  const page = pageOf(list.data)
  // The first page is the query's own; later pages accumulate. Kept separate so a refetch of page one
  // cannot silently truncate what is already on screen.
  const rows = offset === 0 ? page.notifications : [...loaded, ...page.notifications]

  const mayMark = mayWrite('notifications-inbox', user?.systemRole)
  const mayRunCheck = user?.systemRole === 'main_admin'

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetNotificationsUnreadCountQueryKey() })
  }

  // §9.395 - *Skúste to znova* is advice, not a reason. The server's sentence where there is one.
  const failed = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Nepodarilo sa to'),
      description: apiErrorMessage(caught, tt('Skúste to znova.')),
    })

  const markOne = usePostNotificationsIdRead({ mutation: { onSuccess: refresh, onError: failed } })
  const markAll = usePostNotificationsReadAll({
    mutation: {
      onSuccess: () => {
        setOffset(0)
        setLoaded([])
        refresh()
      },
      onError: failed,
    },
  })
  const dailyCheck = usePostNotificationsDailyCheck({
    mutation: {
      onSuccess: (result) => {
        const created = (result as { notificationsCreated?: unknown } | undefined)?.notificationsCreated
        setOffset(0)
        setLoaded([])
        refresh()
        toast({
          title: tt('Kontrola dokončená'),
          description:
            typeof created === 'number'
              ? `${tt('Nových oznámení')}: ${formatCount(created)}`
              : tt('Server nepovedal, koľko oznámení vzniklo.'),
        })
      },
      onError: failed,
    },
  })

  const counts = {
    shown: rows.length,
    total: page.total,
    unread:
      typeof (unread.data as { count?: unknown } | undefined)?.count === 'number'
        ? ((unread.data as { count: number }).count)
        : null,
  }

  // §9.348, the owner's C6. What needs doing, above the list rather than inside it: a flat list newest
  // first is right for a few notices and useless for 151, which is what this inbox has held.
  const summary = inboxSummary(loaded)

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Oznámenia')}
        icon={<Bell className="w-5 h-5" />}
        description={countsDescription(counts)}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {mayRunCheck && (
              <button
                type="button"
                data-run-check
                disabled={dailyCheck.isPending}
                onClick={() => setConfirmCheck(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-card border border-border text-sm font-medium text-muted-foreground disabled:opacity-50"
              >
                {dailyCheck.isPending ? <Spinner /> : <RefreshCw className="w-4 h-4" />}
                <T>Spustiť dennú kontrolu</T>
              </button>
            )}
            {counts.unread !== null && counts.unread > 0 && mayMark && (
              <button
                type="button"
                data-mark-all
                disabled={markAll.isPending}
                onClick={() => markAll.mutate()}
                className={cn('flex items-center gap-1.5 px-3 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
              >
                <CheckCircle2 className="w-4 h-4" /> <T>Označiť všetky</T>
              </button>
            )}
          </div>
        }
      />

      {summary !== '' && (
        <div
          className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3"
          data-inbox-summary
        >
          <p className="text-sm font-semibold">{summary}</p>
        </div>
      )}

      <div className="flex gap-2">
        {[
          { id: 'all', label: tt('Všetky'), only: false },
          { id: 'unread', label: tt('Iba neprečítané'), only: true },
        ].map((filter) => (
          <button
            key={filter.id}
            type="button"
            data-filter={filter.id}
            data-filter-active={unreadOnly === filter.only ? 'yes' : 'no'}
            onClick={() => {
              setUnreadOnly(filter.only)
              setOffset(0)
              setLoaded([])
            }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-bold border',
              unreadOnly === filter.only
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground',
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {list.isLoading && rows.length === 0 ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Bell className="w-12 h-12" />}
          title={tt('Žiadne oznámenia')}
          description={
            unreadOnly ? tt('Všetko je prečítané.') : tt('Systém si zatiaľ nič nevšimol.')
          }
        />
      ) : (
        <div className="space-y-2">
          {rows.map((notification) => (
            <Row
              key={notification.id}
              notification={notification}
              mayMark={mayMark}
              onMark={() => markOne.mutate({ id: notification.id })}
            />
          ))}

          {hasMore(counts) && (
            <button
              type="button"
              data-load-more
              disabled={list.isFetching}
              onClick={() => {
                setLoaded(rows)
                setOffset(rows.length)
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-border text-sm font-bold text-muted-foreground disabled:opacity-50 mt-3"
            >
              {list.isFetching ? <Spinner /> : <RefreshCw className="w-4 h-4" />}{' '}
              <T>Načítať ďalších</T> {formatCount(nextPageSize(counts))}
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmCheck}
        onClose={() => setConfirmCheck(false)}
        title={tt('Spustiť dennú kontrolu teraz?')}
        description={dailyCheckWarning()}
        confirmLabel={tt('Spustiť')}
        icon={<RefreshCw className="w-5 h-5" />}
        loading={dailyCheck.isPending}
        onConfirm={() => {
          setConfirmCheck(false)
          dailyCheck.mutate()
        }}
      />
    </div>
  )
}

const SEVERITY: Record<Severity, { className: string; icon: typeof Info }> = {
  info: { className: 'bg-blue-500/10 border-blue-500/30 text-blue-500', icon: Info },
  warning: { className: 'bg-amber-500/10 border-amber-500/30 text-amber-500', icon: AlertTriangle },
  error: { className: 'bg-red-500/10 border-red-500/30 text-red-500', icon: AlertCircle },
}

function Row({
  notification,
  mayMark,
  onMark,
}: {
  notification: Notification
  mayMark: boolean
  onMark: () => void
}) {
  const style = SEVERITY[notification.severity]
  const Icon = style.icon
  const link = linkFor(notification)
  const isRead = notification.readAt !== null

  return (
    <div
      data-notification={notification.id}
      data-severity={notification.severity}
      data-read={isRead ? 'yes' : 'no'}
      className={cn(
        'flex items-start gap-3 px-4 py-3 rounded-xl border',
        style.className,
        isRead && 'opacity-60',
      )}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-bold uppercase tracking-wider opacity-70"
            data-kind={isLabelledKind(notification.kind) ? 'labelled' : 'raw'}
          >
            {kindLabel(notification.kind)}
          </span>
          <span className="text-[10px] opacity-60">{formatDateTime(notification.createdAt)}</span>
        </div>
        {/* FB-147. A generated title is data, not a phrase to translate. */}
        <p className="font-bold text-sm">{notification.title}</p>
        {notification.body !== null && <p className="text-xs opacity-90 mt-0.5">{notification.body}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {link !== null && (
          <Link
            href={link}
            data-open
            aria-label={tt('Zobraziť')}
            onClick={() => {
              if (!isRead && mayMark) onMark()
            }}
            className="p-1.5 rounded-lg hover:bg-black/10"
          >
            <Eye className="w-4 h-4" />
          </Link>
        )}
        {!isRead && mayMark && (
          <button
            type="button"
            data-mark-read
            aria-label={tt('Označiť ako prečítané')}
            onClick={onMark}
            className="p-1.5 rounded-lg hover:bg-black/10"
          >
            <CheckCircle2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
